import React, {useEffect, useState, useCallback} from "react";
import {NavLink, useLocation} from "react-router-dom";
import {useBranding} from "../../context/BrandingContext";
import useThemeColors from "../organization/portal/useThemeColors";
import useTranslation from "../../i18n/useTranslation";
import useActiveProfile from "./hooks/useActiveProfile";
import BrandName from "./BrandName";

const SIDEBAR_SECTIONS_STORAGE_KEY = "kbola-sidebar-sections";

function getStoredSidebarSections() {
    if (typeof window === "undefined") return {};
    try {
        return JSON.parse(window.localStorage.getItem(SIDEBAR_SECTIONS_STORAGE_KEY) || "{}");
    } catch {
        return {};
    }
}

function Sidebar() {
    const location = useLocation();

    const {theme, ready} = useBranding();
    const colors = useThemeColors();
    const {t} = useTranslation();
    const {authData} = useActiveProfile();
    const isStaff = Boolean(authData?.user?.isStaff);

    const routeStartsWith = useCallback(
        (prefix) =>
            location.pathname === prefix ||
            location.pathname.startsWith(prefix + "/"),
        [location.pathname]
    );

    const activeSection = routeStartsWith("/management") || routeStartsWith("/officials") || routeStartsWith("/settings/staff") || routeStartsWith("/settings/venues")
        ? "management"
        : routeStartsWith("/marketplace") || routeStartsWith("/advertise")
            ? "commerce"
        : routeStartsWith("/settings")
            ? "settings"
            : routeStartsWith("/admin")
                ? "admin"
                : null;

    const [open, setOpen] = useState(() => ({
        ...getStoredSidebarSections(),
        management: routeStartsWith("/management") || routeStartsWith("/officials") || routeStartsWith("/settings/staff") || routeStartsWith("/settings/venues"),
        commerce: routeStartsWith("/marketplace") || routeStartsWith("/advertise"),
        settings: routeStartsWith("/settings"),
        subscription: routeStartsWith("/subscription"),
        advertising: routeStartsWith("/advertise"),
        admin: routeStartsWith("/admin"),
    }));

    useEffect(() => {
        if (routeStartsWith("/subscription")) {
            setOpen((previous) => Object.keys(previous).reduce((next, section) => ({
                ...next,
                [section]: false,
            }), {}));
        } else if (activeSection) {
            setOpen((previous) => Object.keys(previous).reduce((next, section) => ({
                ...next,
                [section]: section === activeSection,
            }), {}));
        }
    }, [activeSection, routeStartsWith]);

    useEffect(() => {
        try {
            window.localStorage.setItem(SIDEBAR_SECTIONS_STORAGE_KEY, JSON.stringify(open));
        } catch {
            return undefined;
        }
    }, [open]);

    if (!ready) {
        return null;
    }

    const toggle = (key) => {
        setOpen((previous) => {
            const willOpen = !previous[key];
            return Object.keys(previous).reduce((next, section) => ({
                ...next,
                [section]: willOpen && section === key,
            }), {});
        });
    };

    const linkClass = "nav-link";

    // 👇 here’s the important part
    const linkStyle = ({isActive}) => ({
        color: isActive ? colors.textOnPrimary : colors.primary,
        backgroundColor: isActive ? colors.primary : "transparent",
        borderLeft: isActive
            ? `3px solid ${colors.accent || colors.primary}`
            : "3px solid transparent",
    });

    const logoSrc =
        theme.logo.sidebar ||
        theme.logo.long ||
        theme.logo.short ||
        "/dist/img/angeonx-sports.png";

    return (
        <aside
            className="main-sidebar elevation-4 kbola-sidebar"
            style={{
                backgroundColor: colors.surface,
                color: colors.textOnSurface,
                borderRight: `1px solid ${
                    colors.border || "rgba(0,0,0,0.4)"
                }`,
            }}
        >
            <div className="text-center" style={{padding: "12px 8px 4px"}}>
                <NavLink
                    to="/management/organizations"
                    className="brand-link border-0"
                    style={{alignItems: "center", display: "flex", flexDirection: "column"}}
                >
                    <img
                        src={logoSrc}
                        alt="AngeonX Sports"
                        style={{
                            height: "140px",
                            objectFit: "contain",
                        }}
                    />
                    <BrandName
                        className="public-footer__brand-name"
                        size="1.5rem"
                        sportsStyle={{fontStyle: "bold", marginLeft: 4}}
                    />
                </NavLink>
            </div>

            <div className="sidebar">
                <nav className="mt-2">
                    <ul
                        className="nav nav-pills nav-sidebar flex-column"
                        role="menu"
                    >
                        {/* MANAGEMENT */}
                        <li className="nav-header kbola-nav-header kbola-section-header">
                            <button type="button" className={`nav-header kbola-section-toggle ${activeSection === "management" ? "is-active" : ""}`} onClick={() => toggle("management")} aria-expanded={open.management} style={{color: colors.textOnSurface, background: "transparent"}}>
                                <strong style={{color: colors.primary, textTransform: "uppercase"}}>{t('sidebar.management')}</strong>
                                <i className={`fas ${open.management ? "fa-angle-up" : "fa-angle-down"}`} style={{marginLeft: 6, color: colors.primary}} />
                            </button>
                        </li>

                        {open.management && (
                            <>
                                <li className="nav-item">
                                    <NavLink to="/management/organizations" className={linkClass} style={linkStyle}>
                                        <i className="nav-icon fas fa-shield-alt"/>
                                        <p>{t('sidebar.organizations')}</p>
                                    </NavLink>
                                </li>
                                <li className="nav-item">
                                    <NavLink to="/management/players" className={linkClass} style={linkStyle}>
                                        <i className="nav-icon fas fa-users"/>
                                        <p>{t('sidebar.players')}</p>
                                    </NavLink>
                                </li>
                                <li className="nav-item">
                                    <NavLink to="/management/financial" className={linkClass} style={linkStyle}>
                                        <i className="nav-icon fas fa-coins"/>
                                        <p>{t('sidebar.financialDashboard')}</p>
                                    </NavLink>
                                </li>
                                <li className="nav-item">
                                    <NavLink to="/management/staff" className={linkClass} style={linkStyle}>
                                        <i className="nav-icon fas fa-user-friends"/>
                                        <p>{t('sidebar.staff')}</p>
                                    </NavLink>
                                </li>
                                <li className="nav-item">
                                    <NavLink to="/management/referees" className={linkClass} style={linkStyle}>
                                        <i className="nav-icon fas fa-id-badge"/>
                                        <p>{t('sidebar.officials')}</p>
                                    </NavLink>
                                </li>
                                <li className="nav-item">
                                    <NavLink to="/management/venues" className={linkClass} style={linkStyle}>
                                        <i className="nav-icon fas fa-map-marker-alt"/>
                                        <p>{t('sidebar.venue')}</p>
                                    </NavLink>
                                </li>
                                <li className="nav-item">
                                    <NavLink to="/management/assignments" className={linkClass} style={linkStyle}>
                                        <i className="nav-icon fas fa-calendar-check"/>
                                        <p>{t('sidebar.assignments')}</p>
                                    </NavLink>
                                </li>
                            </>
                        )}

                        <li className="nav-header kbola-nav-header kbola-section-header">
                            <button type="button" className={`nav-header kbola-section-toggle ${activeSection === "commerce" ? "is-active" : ""}`} onClick={() => toggle("commerce")} aria-expanded={open.commerce} style={{color: colors.textOnSurface, background: "transparent"}}>
                                <strong style={{color: colors.primary, textTransform: "uppercase"}}>{t('sidebar.commerce')}</strong>
                                <i className={`fas ${open.commerce ? "fa-angle-up" : "fa-angle-down"}`} style={{marginLeft: 6, color: colors.primary}} />
                            </button>
                        </li>

                        {open.commerce && (
                            <>
                                <li className="nav-item"><NavLink to="/marketplace" className={linkClass} style={linkStyle}><i className="nav-icon fas fa-store"/><p>{t('sidebar.marketplace')}</p></NavLink></li>
                                <li className="nav-item"><NavLink to="/advertise/campaign" className={linkClass} style={linkStyle}><i className="nav-icon fas fa-bullseye"/><p>{t('sidebar.campaigns')}</p></NavLink></li>
                                <li className="nav-item"><NavLink to="/advertise/audience" className={linkClass} style={linkStyle}><i className="nav-icon fas fa-users"/><p>{t('sidebar.audience')}</p></NavLink></li>
                                <li className="nav-item"><NavLink to="/advertise/sponsor" className={linkClass} style={linkStyle}><i className="nav-icon fas fa-user-shield"/><p>{t('sidebar.sponsors')}</p></NavLink></li>
                                <li className="nav-item"><NavLink to="/advertise/dashboard" className={linkClass} style={linkStyle}><i className="nav-icon fas fa-chart-pie"/><p>{t('sidebar.adsDashboard')}</p></NavLink></li>
                            </>
                        )}

                        {/* SETTINGS */}
                        <li className="nav-header kbola-nav-header kbola-section-header">
                            <button
                                type="button"
                                className={`nav-header kbola-section-toggle ${activeSection === "settings" ? "is-active" : ""}`}
                                onClick={() => toggle("settings")}
                                aria-expanded={open.settings}
                                style={{
                                    color: colors.textOnSurface,
                                    background: "transparent",
                                }}
                            >
                                <strong
                                    style={{
                                        color: colors.primary,
                                        textTransform: "uppercase",
                                    }}
                                >
                                    {t('sidebar.settings')}
                                </strong>
                                <i
                                    className={`fas ${
                                        open.settings
                                            ? "fa-angle-up"
                                            : "fa-angle-down"
                                    }`}
                                    style={{
                                        marginLeft: 6,
                                        color: colors.primary,
                                    }}
                                />
                            </button>
                        </li>

                        {open.settings && (
                            <>
                                <li className="nav-item">
                                    <NavLink
                                        to="/settings/waivers"
                                        className={linkClass}
                                        style={linkStyle}
                                    >
                                        <i className="nav-icon fas fa-signature"/>
                                        <p>
                                            {t('sidebar.waivers')}
                                        </p>
                                    </NavLink>
                                </li>

                                <li className="nav-item">
                                    <NavLink
                                        to="/settings/sanction-rules"
                                        className={linkClass}
                                        style={linkStyle}
                                    >
                                        <i className="nav-icon fas fa-scroll"/>
                                        <p>
                                            {t('sidebar.sanctions')}
                                        </p>
                                    </NavLink>
                                </li>

                                <li className="nav-item">
                                    <NavLink
                                        to="/settings/bank-accounts"
                                        className={linkClass}
                                        style={linkStyle}
                                    >
                                        <i className="nav-icon fas fa-file-invoice-dollar"/>
                                        <p>{t('sidebar.payoutAccounts')}</p>
                                    </NavLink>
                                </li>

                                <li className="nav-item">
                                    <NavLink to="/settings/stream-providers" className={linkClass} style={linkStyle}>
                                        <i className="nav-icon fas fa-video"/>
                                        <p>{t("sidebar.streamingProviders")}</p>
                                    </NavLink>
                                </li>
                            </>
                        )}

                        {isStaff && (
                            <>
                                <li className="nav-header kbola-nav-header kbola-section-header">
                                    <button
                                        type="button"
                                        className={`nav-header kbola-section-toggle ${activeSection === "admin" ? "is-active" : ""}`}
                                        onClick={() => toggle("admin")}
                                        aria-expanded={open.admin}
                                        style={{
                                            color: colors.textOnSurface,
                                            background: "transparent",
                                        }}
                                    >
                                        <span
                                            style={{
                                                color: colors.primary,
                                                textTransform: "uppercase",
                                            }}
                                        >
                                            {t('sidebar.admin')}
                                        </span>
                                        <i
                                            className={`fas ${
                                                open.admin
                                                    ? "fa-angle-up"
                                                    : "fa-angle-down"
                                            }`}
                                            style={{
                                                marginLeft: 6,
                                                color: colors.primary,
                                            }}
                                        />
                                    </button>
                                </li>

                                {open.admin && (
                                    <>
                                        <li className="nav-item">
                                            <NavLink to="/admin/management-data" className={linkClass} style={linkStyle}>
                                                <i className="nav-icon fas fa-chart-line"/><p>{t('sidebar.managementData')}</p>
                                            </NavLink>
                                        </li>
                                        <li className="nav-item">
                                            <NavLink to="/admin/marketplace" className={linkClass} style={linkStyle}>
                                                <i className="nav-icon fas fa-store"/><p>Marketplace moderation</p>
                                            </NavLink>
                                        </li>
                                    </>
                                )}
                            </>
                        )}
                    </ul>
                </nav>
            </div>
        </aside>
    );
}

export default Sidebar;
